import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logPOStatusChange } from '@/utils/activityLogger';

const ADMIN_CHANGEABLE_STATUSES = [
  { value: 'uploaded', label: 'Uploaded' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'funded', label: 'Funded' },
  { value: 'rejected', label: 'Rejected' },
];

interface POStatusSelectorProps {
  poId: string;
  poNumber: string;
  currentStatus: string;
  onStatusChange: () => void;
}

const POStatusSelector: React.FC<POStatusSelectorProps> = ({
  poId,
  poNumber,
  currentStatus,
  onStatusChange,
}) => {
  const { toast } = useToast();

  const handleStatusChange = async (newStatus: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userEmail = userData.user?.email || 'unknown';

      const updatePayload: {
        status: string;
        archive_disabled?: boolean;
      } = { status: newStatus };

      if (newStatus === 'under_review') {
        updatePayload.archive_disabled = true;
      }

      const { error } = await supabase
        .from('purchase_orders')
        .update(updatePayload)
        .eq('id', poId);

      if (error) throw error;

      await logPOStatusChange(
        userEmail,
        poId,
        poNumber,
        currentStatus,
        newStatus
      );

      toast({
        title: 'Status Updated',
        description: `Status changed to ${newStatus.replace(/_/g, ' ')}`,
      });
      onStatusChange();
    } catch (error) {
      console.error('Error updating PO status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const displayStatus = currentStatus === 'pending' ? 'uploaded' : currentStatus;

  return (
    <Select value={displayStatus} onValueChange={handleStatusChange}>
      <SelectTrigger className="w-[140px] h-8 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-popover">
        {ADMIN_CHANGEABLE_STATUSES.map((status) => (
          <SelectItem key={status.value} value={status.value}>
            {status.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default POStatusSelector;
